import React, { useState, useCallback, useEffect } from 'react';
import api from '../../api';
import AdminSectionHeader from '../../components/admin/AdminSectionHeader';
import StatusMessage from '../../components/admin/StatusMessage';
import GalleryItemRow from '../../components/admin/GalleryItemRow';
import ConfirmationModal from '../../components/admin/ConfirmationModal';
import MultiFileUploader from '../../components/admin/MultiFileUploader';
import '../GalleryPage.css';

const CURRENT_YEAR = new Date().getFullYear();
const GALLERY_YEARS = [...Array(2050 - 2020 + 1)].map((_, i) => 2020 + i);

export default function GalleryPage() {
  const [form, setForm] = useState({
    event_id: null,
    event_name: '',
    year: CURRENT_YEAR,
    item_name: '',
    media_type: 'photo',
    description: '',
  });
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [galleryItems, setGalleryItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [filters, setFilters] = useState({
    year: '',
    event_id: '',
    media_type: '',
  });
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [selectedEventName, setSelectedEventName] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [singleDeleteTarget, setSingleDeleteTarget] = useState(null);
  const [singleDeleteOpen, setSingleDeleteOpen] = useState(false);

  const showMsg = (type, text, duration = 3000) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: '', text: '' }), duration);
  };

  // Toggle selection for bulk delete
  const handleToggleSelect = useCallback((itemId) => {
    setSelectedIds(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      }
      return [...prev, itemId];
    });
  }, []);

  // Clear selection
  const handleClearSelection = useCallback(() => {
    setSelectedIds([]);
  }, []);

  // Trigger bulk delete confirmation
  const handleBulkDeleteClick = useCallback(() => {
    if (selectedIds.length > 0) {
      setBulkDeleteConfirmOpen(true);
    }
  }, [selectedIds]);
  // Handle single item delete click
  const handleDeleteClick = useCallback((itemId) => {
    const item = galleryItems.find(i => i.id === itemId);
    setSingleDeleteTarget(item);
    setSingleDeleteOpen(true);
  }, [galleryItems]);

  // Confirm and execute single delete
  const handleSingleDeleteConfirm = useCallback(() => {
    setSingleDeleteOpen(false);
    if (!singleDeleteTarget) return;

    const deleteId = singleDeleteTarget.id;
    api.delete(`/api/gallery/items/${deleteId}`)
      .then(() => {
        showMsg('success', 'Item deleted');
        // Remove from local state immediately
        setGalleryItems(prev => prev.filter(item => item.id !== deleteId));
        // Also clear from selection if it was selected
        setSelectedIds(prev => prev.filter(id => id !== deleteId));
      })
      .catch(() => {
        showMsg('error', 'Failed to delete item');
      });
    setSingleDeleteTarget(null);
  }, [singleDeleteTarget, showMsg]);

  // Confirm and execute bulk delete
  const handleBulkDeleteConfirm = useCallback(() => {
    setBulkDeleteConfirmOpen(false);
    if (selectedIds.length === 0) return;

    const deletePromises = selectedIds.map(id =>
      api.delete(`/api/gallery/items/${id}`)
    );

    Promise.all(deletePromises)
      .then(() => {
        showMsg('success', `${selectedIds.length} item(s) deleted`);
        setSelectedIds([]);
        // Refresh items list
        setItemsLoading(true);
        return api.get('/api/gallery/items', {
          params: {
            year: filters.year || undefined,
            event_id: filters.event_id || undefined,
            media_type: filters.media_type || undefined,
          },
        });
      })
      .then((res) => {
        if (Array.isArray(res.data)) setGalleryItems(res.data);
      })
      .catch(() => {
        showMsg('error', 'Failed to delete some items');
      })
      .finally(() => setItemsLoading(false));
  }, [selectedIds, filters]);

  // Load events for picker
  useEffect(() => {
    setEventsLoading(true);
    api
      .get('/api/admin/events?include_past=true')
      .then((res) => {
        if (Array.isArray(res.data)) setEvents(res.data);
      })
      .catch(() => {})
      .finally(() => setEventsLoading(false));
  }, []);

  // Auto-fill year when event selected
  useEffect(() => {
    if (form.event_id) {
      const ev = events.find((e) => e.id === form.event_id);
      if (ev && ev.date_time) {
        const year = new Date(ev.date_time).getFullYear();
        setForm((prev) => ({ ...prev, year }));
        setSelectedEventName(ev.event_name);
      }
    } else {
      setSelectedEventName('');
    }
  }, [form.event_id, events]);

  // Load gallery items with filters
  useEffect(() => {
    setItemsLoading(true);
    api
      .get('/api/gallery/items', {
        params: {
          year: filters.year || undefined,
          event_id: filters.event_id || undefined,
          media_type: filters.media_type || undefined,
        },
      })
      .then((res) => {
        if (Array.isArray(res.data)) setGalleryItems(res.data);
      })
      .catch(() => {
        showMsg('error', 'Failed to load gallery items');
      })
      .finally(() => setItemsLoading(false));
  }, [filters.year, filters.event_id, filters.media_type]);

  const handleUploadSuccess = useCallback(
    (file, response) => {
      showMsg('success', `Uploaded ${file.name}`);
      // If thumbnail was generated, we can optionally notify the user
      if (response.thumbnail_url) {
        console.log(`Thumbnail generated for ${file.name}: ${response.thumbnail_url}`);
      }
    },
    [showMsg]
  );

  const handleUploadError = useCallback(
    (file, error) => {
      console.error('Upload error:', error);
      showMsg(
        'error',
        `Failed to upload ${file.name}: ${
          error.response?.data?.detail || error.message || 'Unknown error'
        }`
      );
    },
    [showMsg]
  );

  const handleFiltersChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setFilters((prev) => ({ ...prev, [name]: value }));
    },
    []
  );

  const handleResetFilters = useCallback(() => {
    setFilters({ year: '', event_id: '', media_type: '' });
  }, []);

  const commonFields = {
    year: form.year,
    event_id: form.event_id,
    event_name: form.event_name || selectedEventName || '',
    item_name: form.item_name,
    media_type: form.media_type,
    description: form.description,
  };

  return (
    <div className="admin-gallery-page">
      <AdminSectionHeader
        title="Gallery Uploads"
        subtitle="Upload media to the gallery for events and programs"
      />
      {msg.text && <StatusMessage type={msg.type} message={msg.text} />}

      <section className="admin-section">
        <h3>Add Gallery Item</h3>

        <form className="admin-gallery-form" onSubmit={(e) => e.preventDefault()}>
          <div className="admin-gallery-form-row">
            <div>
              <label>
                Year
                <select
                  value={form.year}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, year: Number(e.target.value) || CURRENT_YEAR }))
                  }
                  required
                >
                  {GALLERY_YEARS.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </label>
            </div>
            <div>
              <label>
                Event
                <select
                  value={form.event_id ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setForm((prev) => ({
                      ...prev,
                      event_id: val === '' ? null : String(val),
                      event_name: ''
                    }));
                  }}
                  disabled={eventsLoading}
                >
                  <option value="">Select an event…</option>
                  {events.filter((e) => !e.is_cancelled).map((e) => (
                    <option key={e.id} value={e.id}>{e.event_name}</option>
                  ))}
                </select>
                {eventsLoading && (
                  <span style={{ fontSize: '0.8rem', color: '#666', marginLeft: '0.5rem' }}>
                    loading…
                  </span>
                )}
              </label>
              {!form.event_id && (
                <div style={{ marginTop: '0.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem' }}>
                    Or enter event name (ad-hoc upload)
                    <input
                      type="text"
                      value={form.event_name}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, event_name: e.target.value.trim() }))
                      }
                      placeholder="Free-text event name"
                    />
                  </label>
                  {selectedEventName && (
                    <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: '#666' }}>
                      Using event: <strong>{selectedEventName}</strong>
                    </p>
                  )}
                </div>
              )}
            </div>
            <div>
              <label>
                Item name
                <input
                  type="text"
                  value={form.item_name}
                  onChange={(e) => setForm((prev) => ({ ...prev, item_name: e.target.value.trim() }))}
                  placeholder="Defaults to filename"
                />
              </label>
            </div>
            <div>
              <label>
                Media type
                <select
                  value={form.media_type}
                  onChange={(e) => setForm((prev) => ({ ...prev, media_type: e.target.value }))}
                >
                  <option value="photo">Photo</option>
                  <option value="video">Video</option>
                </select>
              </label>
            </div>
            <div>
              <label>
                Description (optional)
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                  rows={2}
                  placeholder="Brief description of the media"
                />
              </label>
            </div>
          </div>
        </form>

        <MultiFileUploader
          endpoint="/api/gallery/upload"
          commonFields={commonFields}
          onSuccess={handleUploadSuccess}
          onError={handleUploadError}
          disabled={itemsLoading || !(form.year || form.event_id || form.event_name)}
        />

        <div className="admin-gallery-table-wrap">
          <h3>Existing Gallery Items</h3>
          <div className="admin-gallery-filters">
            <div>
              <label>
                Filter by year:{' '}
                <select
                  name="year"
                  value={filters.year}
                  onChange={handleFiltersChange}
                >
                  <option value="">All years</option>
                  {GALLERY_YEARS.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </label>
            </div>
            <div>
              <label>
                Filter by event:{' '}
                <select
                  name="event_id"
                  value={filters.event_id}
                  onChange={handleFiltersChange}
                >
                  <option value="">All events</option>
                  {events.filter((e) => !e.is_cancelled).map((e) => (
                    <option key={e.id} value={e.id}>{e.event_name}</option>
                  ))}
                </select>
              </label>
            </div>
            <div>
              <label>
                Filter by type:{' '}
                <select
                  name="media_type"
                  value={filters.media_type}
                  onChange={handleFiltersChange}
                >
                  <option value="">All types</option>
                  <option value="photo">Photo</option>
                  <option value="video">Video</option>
                </select>
              </label>
            </div>
            <button type="button" onClick={handleResetFilters} className="btn btn-sm btn-secondary">
              Reset filters
            </button>
          </div>

          {itemsLoading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              Loading gallery items…
            </div>
          ) : galleryItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              No gallery items match the current filters.
            </div>
          ) : (
            <>
              {selectedIds.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <span style={{ fontSize: '0.9rem', color: '#666' }}>
                    {selectedIds.length} item(s) selected
                  </span>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={handleClearSelection}
                    >
                      Clear Selection
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={handleBulkDeleteClick}
                      disabled={bulkDeleteConfirmOpen}
                    >
                      Delete Selected
                    </button>
                  </div>
                </div>
              )}
              <table className="admin-gallery-table">
                <thead>
                  <tr>
                    <th style={{ width: '60px' }}>Thumb</th>
                    <th>Item</th>
                    <th className="hide-mobile">Event</th>
                    <th className="hide-mobile">Year</th>
                    <th>Type</th>
                    <th className="hide-mobile">Size</th>
                    <th className="hide-mobile">Uploaded</th>
                    <th style={{ width: '60px', textAlign: 'center' }}>Select</th>
                    <th style={{ width: '80px', textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {galleryItems.map((item) => (
                    <GalleryItemRow
                      key={item.id}
                      item={item}
                      isSelected={selectedIds.includes(item.id)}
                      onToggleSelect={handleToggleSelect}
                      onDelete={handleDeleteClick}
                      disabled={itemsLoading}
                    />
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </section>
      {/* Single Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={singleDeleteOpen}
        title="Delete Gallery Item"
        message={`Are you sure you want to delete "${singleDeleteTarget?.item_name || 'this item'}"? This will permanently remove the item and its associated media.`}
        onConfirm={handleSingleDeleteConfirm}
        onCancel={() => {
          setSingleDeleteOpen(false);
          setSingleDeleteTarget(null);
        }}
        variant="danger"
      />

      {/* Bulk Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={bulkDeleteConfirmOpen}
        title="Delete Gallery Items"
        message={`Are you sure you want to delete ${selectedIds.length} item(s) permanently?`}
        onConfirm={handleBulkDeleteConfirm}
        onCancel={() => setBulkDeleteConfirmOpen(false)}
        variant="danger"
      />
    </div>
  );
}